<script setup lang="ts">
import DoNotContactMessagingInfo from '@/components/Conversations/DoNotContact/DoNotContactMessagingInfo.vue';
import MessageCompose from '@/components/Conversations/Message/MessageCompose.vue';
import MessageList from '@/components/Conversations/Message/MessageList.vue';
import MeService from '@/core/shared/me/me.service';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import ConversationMessageService from '@/core/conversations/message/conversation-message.service';
import ScrollUtils from '@/utils/scroll-utils';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import OptedOutMessagingPanel from '@/components/Conversations/Message/OptedOutMessagingPanel.vue';
import NoMessageHistoryOptedOut from '@/components/Conversations/Message/NoMessageHistoryOptedOut.vue';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import StagedMessagesWrapper from '@/components/Conversations/StagedMessage/StagedMessagesWrapper/StagedMessagesWrapper.vue';
import ProjectService from '@/core/shared/project/project.service';
import ProjectCopilotConfigurationUtils, {
  DEFAULT_COPILOT_NAME,
} from '@/utils/project/project-copilot-configuration.utils';

const meService = new MeService();
const messageService = new ConversationMessageService();
const conversationIndexService = new ConversationIndexService();
const proProfileService = new ProProfileService();
const projectService = new ProjectService();

const aiRecruiterName = ref<string>(DEFAULT_COPILOT_NAME);
const messagesViewElementRef = ref<HTMLElement>();
const nextPageToFetch = ref(1);
const isMessageListVisible = ref(true);
const messageComposeRef = ref<typeof MessageCompose>();
const isSending = ref(false);

const jobApplicant = computed(() => proProfileService.selectedProApplication);
const jobApplicantJobId = computed(() => jobApplicant.value?.jobId);
const userProfileId = computed(() => meService.userProfile?.id);
const scrollMessagesToBottom = computed(() => messageService.scrollMessagesToBottom);

const currentProFirstName = computed(
  () => conversationIndexService.selectedConversationIndex?.proName?.split(' ')?.[0] ?? '',
);

const currentSelectedConversationId = computed(
  () => conversationIndexService.selectedConversationIndex?.conversationId,
);
const currentProId = computed(() => conversationIndexService.selectedConversationIndex?.proId);
const currentJobApplicantId = computed(() => jobApplicant.value?.id);
const currentProMessageStatus = computed(() => conversationIndexService.selectedProMessageStatus);
const currentEmployerId = computed(() => meService.getSelectedEmployerId());

const canReceiveMessage = computed(
  () => !currentProMessageStatus.value || currentProMessageStatus.value.canReceiveMessage,
);

const isDoNotContact = computed(() => conversationIndexService.isMessagingDoNotContact);
const refreshState = computed(() => conversationIndexService.messageStatusRefreshState);
const showDisabledComposer = computed(() => isDoNotContact.value || refreshState.value !== 'idle');
const isMessagingDisabled = computed(() => conversationIndexService.isMessagingDisabled);
const retryMessageStatus = () => conversationIndexService.refreshSelectedProMessageStatus(true);

const showNoMessageHistoryOptedOutComponent = computed(
  () =>
    isMessageListVisible.value &&
    !messageService.messageList?.length &&
    !canReceiveMessage.value &&
    !showDisabledComposer.value,
);

async function fetchInitialMessages(conversationId: number) {
  nextPageToFetch.value = 1;

  await messageService.fetchConversationMessages(conversationId, {
    page: nextPageToFetch.value,
    clearMessages: true,
  });

  nextPageToFetch.value++;
}

async function fetchExistingMessages(conversationId: number) {
  await messageService.fetchConversationMessages(conversationId, { page: 1 });
}

async function fetchMoreMessages(conversationId: number) {
  const newMessages = await messageService.fetchConversationMessages(conversationId, {
    page: nextPageToFetch.value,
  });

  // The thread may have moved on while the page was in flight; advancing the
  // cursor then would skip a page of the conversation now on screen.
  if (currentSelectedConversationId.value !== conversationId) {
    return;
  }

  if (newMessages.length > 0) {
    nextPageToFetch.value++;
  }
}

const sendMessage = async ({ messageContent }: { messageContent: string }) => {
  if (isMessagingDisabled.value || isSending.value) return;
  if (currentSelectedConversationId.value) {
    isSending.value = true;
    try {
      await messageService.sendMessage(
        currentSelectedConversationId.value,
        messageContent,
        jobApplicantJobId.value,
      );
      messageService.scrollMessagesToBottom = true;
    } finally {
      isSending.value = false;
    }
  }
};

const handleMessageListScroll = async (ev: UIEvent) => {
  if (!currentSelectedConversationId.value) {
    return;
  }

  const isStartOfScroll = ScrollUtils.isStartOfScroll(ev.target);

  if (isStartOfScroll) {
    await fetchMoreMessages(currentSelectedConversationId.value);
  }
};

function clearPollingInterval() {
  if (messageService.messagesPollingIntervalId) {
    window.clearInterval(messageService.messagesPollingIntervalId);
  }
}

const setPollingInterval = (currentlySelectedConversationId: number) => {
  clearPollingInterval();

  messageService.messagesPollingIntervalId = window.setInterval(async () => {
    if (!currentlySelectedConversationId) {
      return;
    }

    await fetchExistingMessages(currentlySelectedConversationId);
    const isEndOfScroll = ScrollUtils.isEndOfScroll(messagesViewElementRef.value);

    if (isEndOfScroll) {
      messageService.scrollMessagesToBottom = true;
    }
  }, 5000); // polling on messages every 5 seconds
};

function handleInsertStagedMessage(message: string) {
  if (isMessagingDisabled.value) return;
  if (messageComposeRef.value) {
    messageComposeRef.value.setMessageContent(message);
  }
}

watch(
  () => currentSelectedConversationId.value,
  async (newConversationId, oldConversationId) => {
    try {
      isMessageListVisible.value = false;

      if (newConversationId && newConversationId !== oldConversationId) {
        clearPollingInterval();
        await proProfileService.getApplicantStatusHistory();
        await fetchInitialMessages(newConversationId);

        // This small timeout will solve an issue when there are too many
        // messages sometimes the scrollToBottom will get "stuck" trying to
        // scroll to the bottom
        setTimeout(() => {
          ScrollUtils.scrollToBottom(messagesViewElementRef.value, 'instant');
        }, 100);

        setPollingInterval(newConversationId);
      }
    } finally {
      isMessageListVisible.value = true;
    }
  },
  { immediate: true },
);
/**
 * Scrolls to the bottom of the message list when the scrollMessagesToBottom flag is true
 * this is a side effect of Bulk messages Modal since that modal does not have access to the `messagesViewElementRef`
 * @param newVal - The new value of the scrollMessagesToBottom flag
 */
watch(scrollMessagesToBottom, (newVal) => {
  if (newVal) {
    ScrollUtils.scrollToBottom(messagesViewElementRef.value, 'smooth');
    messageService.scrollMessagesToBottom = false;
  }
});

watch(
  jobApplicantJobId,
  async (newJobId, oldJobId) => {
    let currentProject = null;

    if (newJobId && newJobId !== oldJobId) {
      currentProject = await projectService.getLocalProjectByJobId(newJobId);
    }

    aiRecruiterName.value = ProjectCopilotConfigurationUtils.getCopilotName(currentProject);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  clearPollingInterval();
});
</script>

<template>
  <div
    class="flex h-full flex-col items-center justify-center text-center"
    v-if="showNoMessageHistoryOptedOutComponent"
  >
    <NoMessageHistoryOptedOut />
  </div>

  <template v-else>
    <div class="flex h-full flex-col">
      <div class="relative h-0 flex-grow overflow-hidden">
        <div
          ref="messagesViewElementRef"
          :class="{
            'h-full overflow-y-auto pb-8 transition-opacity': true,
            'opacity-0': !isMessageListVisible,
            'opacity-100': isMessageListVisible,
          }"
          @scroll="handleMessageListScroll"
        >
          <MessageList
            v-if="userProfileId"
            :messages="messageService.messageList"
            :do-not-contact-history-unavailable="messageService.doNotContactHistoryUnavailable"
            :current-pro-id="currentProId"
            :job-applicant="jobApplicant"
            :user-profile-id="userProfileId"
            :ai-recruiter-name="aiRecruiterName"
          />
        </div>

        <div
          class="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white via-white/75 to-white/0"
        />
      </div>

      <div v-if="!showDisabledComposer" class="w-full px-4 pb-1">
        <StagedMessagesWrapper
          :pro-id="currentProId"
          :employer-id="currentEmployerId"
          @click:insert="handleInsertStagedMessage"
        />
      </div>
      <DoNotContactMessagingInfo
        :do-not-contact="isDoNotContact"
        :refresh-state="refreshState"
        @retry="retryMessageStatus"
      />
      <div class="w-full px-4 pb-4">
        <MessageCompose
          v-if="showDisabledComposer || canReceiveMessage"
          :disabled="isMessagingDisabled"
          :is-sending="isSending"
          ref="messageComposeRef"
          :applicant-id="currentJobApplicantId"
          :pro-name="currentProFirstName"
          @send-message="sendMessage"
        />

        <OptedOutMessagingPanel v-else />
      </div>
    </div>
  </template>
</template>
